// 🛠️ UTILIDADES PARA FECHAS
// Agregar este archivo en: frontend/src/utils/dateUtils.js

/**
 * Convierte una fecha ISO a formato YYYY-MM-DD para inputs type="date"
 * @param {string} isoDate - Fecha en formato ISO (ej: "2025-10-01T03:00:00.000Z")
 * @returns {string} Fecha en formato YYYY-MM-DD (ej: "2025-10-01")
 */
export const formatDateForInput = (isoDate) => {
  // The date part as is. Reading it with new Date() and local getters turned
  // the UTC server's midnight into the day before, and every save of an
  // edit form stored that earlier day.
  return toDateInputValue(isoDate);
};

/**
 * Convierte una fecha de input a formato ISO para enviar al backend
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha en formato ISO
 */
export const formatDateForBackend = (dateString) => {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    return date.toISOString();
  } catch (error) {
    console.error('Error formatting date for backend:', error);
    return null;
  }
};

/**
 * Formatea una fecha para mostrar al usuario
 * @param {string} isoDate - Fecha en formato ISO
 * @param {string} locale - Locale para el formato (default: 'es-ES')
 * @returns {string} Fecha formateada (ej: "1 de octubre de 2025")
 */
export const formatDateForDisplay = (isoDate, locale = 'es-ES') => {
  if (!isoDate) return '';
  
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return '';
  }
};

/**
 * Obtiene la fecha de hoy en formato YYYY-MM-DD
 * @returns {string} Fecha de hoy
 */
export const toLocalISODate = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getTodayDate = () => toLocalISODate(new Date());

/** Monday–Sunday week as local YYYY-MM-DD strings. */
export const getWeekDatesMondayFirst = (ref = new Date()) => {
  const curr = new Date(ref);
  curr.setHours(12, 0, 0, 0);
  const day = curr.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(curr);
  monday.setDate(curr.getDate() + diff);
  const week = [];
  for (let i = 0; i < 7; i++) {
    const next = new Date(monday);
    next.setDate(monday.getDate() + i);
    week.push(toLocalISODate(next));
  }
  return week;
};

/**
 * Valida si una fecha es válida
 * @param {string} dateString - Fecha a validar
 * @returns {boolean} true si es válida
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

/**
 * "YYYY-MM-DD" (or an ISO timestamp) as local midnight of that calendar day.
 * new Date("YYYY-MM-DD") is UTC midnight, which at UTC-3 is 21:00 the day
 * before: getDay() and date pickers then show the previous day.
 * Returns null for empty or unparseable input.
 */
export const parseLocalDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Monday-to-Friday days from start to end, both "YYYY-MM-DD", inclusive. */
export const countWeekdays = (startValue, endValue) => {
  const current = parseLocalDate(startValue);
  const end = parseLocalDate(endValue);
  if (!current || !end) return 0;
  let days = 0;
  while (current <= end) {
    if (current.getDay() !== 0 && current.getDay() !== 6) days++;
    current.setDate(current.getDate() + 1);
  }
  return days;
};

/**
 * "YYYY-MM" of the local calendar month. toISOString().slice(0, 7) is the
 * UTC month: at UTC-3 it turns to the next month at 21:00 on the last day.
 */
export const toLocalMonth = (value = new Date()) => toLocalISODate(value).slice(0, 7);

/**
 * "YYYY-MM-DD" for an <input type="date"> from a DATE column as the API sends
 * it: "YYYY-MM-DD", or "YYYY-MM-DDT00:00:00.000Z" from the UTC server. Takes
 * the date part as is; new Date() would turn that midnight UTC into the day
 * before here, and saving the form would store the earlier day.
 */
export const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : '');

/** "YYYY-MM" n months after the given "YYYY-MM" (before, if n is negative). */
export const addMonths = (month, n) => {
  const [y, m] = month.split('-').map(Number);
  return toLocalMonth(new Date(y, m - 1 + n, 1));
};

/** First and last day, as "YYYY-MM-DD", of a "YYYY-MM" month. */
export const monthBounds = (month) => {
  const [y, m] = month.split('-').map(Number);
  return { start: `${month}-01`, end: toLocalISODate(new Date(y, m, 0)) };
};

/**
 * "YYYY-MM" of a DATE value as the API sends it ("YYYY-MM-DD" or the UTC
 * server's "YYYY-MM-DDT00:00:00.000Z"): the date part, never new Date().
 */
export const monthOf = (value) => String(value || '').slice(0, 7);